import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Shield, Plus, Trash2, Save, Globe, X, Phone, Calendar, Sparkles } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useTenant } from '@/contexts/TenantContext';
import { formatDateTime, vokalSpelling } from '@/lib/utils';
import { QRCodeSVG } from 'qrcode.react';
import { RegistrationFormEditor } from '@/components/settings/RegistrationFormEditor';
import { TeachingConfigEditor } from '@/components/settings/TeachingConfigEditor';
import { CustomFieldsManager } from '@/components/customfields/CustomFieldsManager';
import { refreshClickToDialConfig } from '@/hooks/useClickToDial';
import { formatApiError } from '@/lib/errors';

export default function Settings() {
  const { activeTenantId, activeTenant } = useTenant();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [dial, setDial] = useState({ available: false, api_configured: false, sip_uid: '', can_manage: false });
  const [dialUsers, setDialUsers] = useState([]);
  const [dialUsersError, setDialUsersError] = useState(null);
  const [dialSaving, setDialSaving] = useState(null);
  const [sipTargets, setSipTargets] = useState([]);
  const [sipTargetsError, setSipTargetsError] = useState(null);

  const [reg, setReg] = useState({
    registration_enabled: false,
    registration_intro: '',
    agb_text: '',
    agb_url: '',
    datenschutz_text: '',
    datenschutz_url: '',
    widerruf_text: '',
    widerruf_url: '',
    instrument_categories: [],
    registration_sla_days: 3,
  });
  const [regLoading, setRegLoading] = useState(false);
  const [regSaving, setRegSaving] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [calRooms, setCalRooms] = useState({ enabled: false, detail: 'busy' });
  const [calRoomsLoading, setCalRoomsLoading] = useState(false);
  const [calRoomsSaving, setCalRoomsSaving] = useState(false);
  const [openai, setOpenai] = useState(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState('gpt-5.6-terra');
  const [openaiSaving, setOpenaiSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
    loadOpenai();
  }, []);

  useEffect(() => {
    if (activeTenantId) loadDialConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  useEffect(() => {
    if (activeTenantId) loadRegistrationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  useEffect(() => {
    if (activeTenantId) loadCalendarRoomsSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const loadRegistrationSettings = async () => {
    if (!activeTenantId) return;
    setRegLoading(true);
    try {
      const r = await api.get(`/tenants/${activeTenantId}/registration-settings`);
      setReg({
        registration_enabled: !!r.data.registration_enabled,
        registration_intro: r.data.registration_intro || '',
        agb_text: r.data.agb_text || '',
        agb_url: r.data.agb_url || '',
        datenschutz_text: r.data.datenschutz_text || '',
        datenschutz_url: r.data.datenschutz_url || '',
        widerruf_text: r.data.widerruf_text || '',
        widerruf_url: r.data.widerruf_url || '',
        instrument_categories: Array.isArray(r.data.instrument_categories)
          ? r.data.instrument_categories
          : [],
        registration_sla_days: typeof r.data.registration_sla_days === 'number' ? r.data.registration_sla_days : 3,
      });
    } catch (e) {
      toast.error(formatApiError(e, 'Online-Anmeldung-Einstellungen konnten nicht geladen werden'));
    } finally {
      setRegLoading(false);
    }
  };

  const loadCalendarRoomsSettings = async () => {
    if (!activeTenantId) return;
    setCalRoomsLoading(true);
    try {
      const r = await api.get(`/tenants/${activeTenantId}/calendar-rooms-settings`);
      setCalRooms({
        enabled: !!r.data.enabled,
        detail: r.data.detail === 'course' || r.data.detail === 'students' ? r.data.detail : 'busy',
      });
    } catch (e) {
      toast.error(formatApiError(e, 'Raumkalender-Einstellungen konnten nicht geladen werden'));
    } finally {
      setCalRoomsLoading(false);
    }
  };

  const saveCalendarRoomsSettings = async () => {
    if (!activeTenantId) {
      toast.error('Kein Mandant ausgewählt — bitte oben links den Mandanten wählen.');
      return;
    }
    setCalRoomsSaving(true);
    try {
      const r = await api.put(`/tenants/${activeTenantId}/calendar-rooms-settings`, {
        enabled: !!calRooms.enabled,
        detail: calRooms.detail,
      });
      setCalRooms({
        enabled: !!r.data.enabled,
        detail: r.data.detail === 'course' || r.data.detail === 'students' ? r.data.detail : 'busy',
      });
      toast.success('Raumkalender gespeichert');
    } catch (e) {
      toast.error(formatApiError(e, 'Speichern fehlgeschlagen'));
    } finally {
      setCalRoomsSaving(false);
    }
  };

  const saveRegistrationSettings = async () => {
    if (!activeTenantId) {
      toast.error('Kein Mandant ausgewählt — bitte oben links den Mandanten wählen.');
      return;
    }
    setRegSaving(true);
    try {
      const body = {
        registration_enabled: !!reg.registration_enabled,
        registration_intro: reg.registration_intro || null,
        agb_text: reg.agb_text || null,
        agb_url: reg.agb_url || null,
        datenschutz_text: reg.datenschutz_text || null,
        datenschutz_url: reg.datenschutz_url || null,
        widerruf_text: reg.widerruf_text || null,
        widerruf_url: reg.widerruf_url || null,
        instrument_categories: reg.instrument_categories,
        registration_sla_days: Math.min(30, Math.max(0, parseInt(reg.registration_sla_days, 10) || 0)),
      };
      const r = await api.put(`/tenants/${activeTenantId}/registration-settings`, body);
      setReg({
        registration_enabled: !!r.data.registration_enabled,
        registration_intro: r.data.registration_intro || '',
        agb_text: r.data.agb_text || '',
        agb_url: r.data.agb_url || '',
        datenschutz_text: r.data.datenschutz_text || '',
        datenschutz_url: r.data.datenschutz_url || '',
        widerruf_text: r.data.widerruf_text || '',
        widerruf_url: r.data.widerruf_url || '',
        instrument_categories: Array.isArray(r.data.instrument_categories)
          ? r.data.instrument_categories
          : [],
        registration_sla_days: typeof r.data.registration_sla_days === 'number' ? r.data.registration_sla_days : 3,
      });
      toast.success('Online-Anmeldung gespeichert');
    } catch (e) {
      toast.error(formatApiError(e, 'Speichern fehlgeschlagen'));
    } finally {
      setRegSaving(false);
    }
  };

  const addInstrumentCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    if (reg.instrument_categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.error('Kategorie existiert bereits');
      return;
    }
    setReg((p) => ({
      ...p,
      instrument_categories: [...p.instrument_categories, { name, items: [] }],
    }));
    setNewCatName('');
  };

  const removeInstrumentCategory = (idx) => {
    setReg((p) => ({
      ...p,
      instrument_categories: p.instrument_categories.filter((_, i) => i !== idx),
    }));
  };

  const addInstrumentToCategory = (catIdx, value) => {
    const v = (value || '').trim();
    if (!v) return;
    setReg((p) => {
      const next = [...p.instrument_categories];
      const items = next[catIdx]?.items || [];
      if (items.some((i) => i.toLowerCase() === v.toLowerCase())) return p;
      next[catIdx] = { ...next[catIdx], items: [...items, v] };
      return { ...p, instrument_categories: next };
    });
  };

  const removeInstrumentFromCategory = (catIdx, itemIdx) => {
    setReg((p) => {
      const next = [...p.instrument_categories];
      next[catIdx] = {
        ...next[catIdx],
        items: next[catIdx].items.filter((_, i) => i !== itemIdx),
      };
      return { ...p, instrument_categories: next };
    });
  };

  const loadPreferences = async () => {
    try {
      const r = await api.get('/auth/me/preferences');
      setEmailNotifications(!!r.data.email_notifications);
    } catch (e) {
      // silent
    }
  };

  const loadOpenai = async () => {
    try {
      const r = await api.get('/settings/openai');
      if (r.data && typeof r.data === 'object' && !Array.isArray(r.data) && 'configured' in r.data) {
        setOpenai(r.data);
        setOpenaiModel(r.data.model || 'gpt-5.6-terra');
      }
    } catch {
      setOpenai(null);
    }
  };

  const saveOpenai = async (clear = false) => {
    setOpenaiSaving(true);
    try {
      const body = { model: openaiModel };
      if (clear) body.clear = true;
      else if (openaiKey.trim()) body.api_key = openaiKey.trim();
      const r = await api.put('/settings/openai', body);
      setOpenai(r.data);
      setOpenaiKey('');
      toast.success(clear ? 'API-Schlüssel entfernt' : 'KI-Einstellungen gespeichert');
    } catch (e) {
      toast.error(formatApiError(e, 'Speichern fehlgeschlagen'));
    } finally {
      setOpenaiSaving(false);
    }
  };

  const loadDialConfig = async () => {
    try {
      const r = await api.get('/telephony/dial/config');
      setDial({ ...r.data, sip_uid: r.data.sip_uid || '' });
      if (r.data.can_manage) {
        loadDialUsers();
        loadSipTargets();
      }
    } catch {
      // silent
    }
  };

  const loadSipTargets = async () => {
    setSipTargetsError(null);
    try {
      const r = await api.get('/telephony/dial/sip-users');
      setSipTargets(r.data.sip_users || []);
    } catch (e) {
      // Ohne Liste bleibt die freie Eingabe als Rückfall bestehen.
      setSipTargets([]);
      setSipTargetsError(e?.response?.data?.detail || 'Die VoIP-Ziele konnten nicht geladen werden.');
    }
  };

  const loadDialUsers = async () => {
    setDialUsersError(null);
    try {
      const r = await api.get('/telephony/dial/users');
      setDialUsers(r.data.users || []);
    } catch (e) {
      // Ohne Meldung sähe ein Serverfehler aus wie ein leerer Mandant.
      setDialUsers([]);
      setDialUsersError(e?.response?.data?.detail || 'Die Liste konnte nicht geladen werden.');
    }
  };

  const setSipUidDraft = (userId, value) =>
    setDialUsers((list) => list.map((u) => (u.id === userId ? { ...u, sip_uid: value } : u)));

  const saveSipUidFor = async (userId, value) => {
    setDialSaving(userId);
    try {
      await api.put(`/telephony/dial/users/${userId}`, { sip_uid: value });
      refreshClickToDialConfig(activeTenantId);
      loadDialConfig();
      toast.success(value ? 'Kennung gespeichert.' : 'Kennung entfernt.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setDialSaving(null);
    }
  };

  const toggleEmailNotifications = async () => {
    try {
      const newValue = !emailNotifications;
      await api.put('/auth/me/preferences', { email_notifications: newValue });
      setEmailNotifications(newValue);
      toast.success(`E-Mail-Hinweise ${newValue ? 'aktiviert' : 'deaktiviert'}`);
    } catch {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" data-testid="settings-title">
            Einstellungen
          </h1>
          <p className="text-slate-600">Systemeinstellungen und Konfiguration</p>
        </div>

        {openai && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="settings-openai">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-brand-red/5 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-brand-red" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">KI / ChatGPT</h2>
              <p className="text-sm text-slate-500">
                Eigenen OpenAI-Schlüssel für die Sprachsteuerung — ohne ihn bleibt nur der starre Befehlskatalog
              </p>
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <p className="text-sm text-slate-600">
              Das ChatGPT-Abo auf chatgpt.com reicht nicht. Du brauchst einen{' '}
              <strong>API-Schlüssel</strong> von{' '}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noreferrer"
                className="text-brand-red underline font-medium"
              >
                platform.openai.com/api-keys
              </a>
              {' '}(kostenpflichtig nach Verbrauch).
            </p>
            <div className="text-sm">
              <span className="text-slate-500">Aktuell: </span>
              {openai.configured ? (
                <span className="text-emerald-700">
                  {openai.source === 'settings' && openai.hint
                    ? `Schlüssel hinterlegt (${openai.hint})`
                    : openai.source === 'env'
                      ? 'Schlüssel kommt vom Server (OPENAI_API_KEY)'
                      : openai.source === 'emergent'
                        ? 'Emergent-Fallback aktiv — besser den eigenen Schlüssel eintragen'
                        : 'Schlüssel aktiv'}
                </span>
              ) : (
                <span className="text-amber-700">kein Schlüssel — Sprachsteuerung versteht nur feste Formulierungen</span>
              )}
            </div>
            <div>
              <Label htmlFor="settings-openai-key">API-Schlüssel</Label>
              <Input
                id="settings-openai-key"
                type="password"
                autoComplete="off"
                data-testid="settings-openai-key"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder={openai.hint ? `Hinterlegt: ${openai.hint}` : 'sk-…'}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="settings-openai-model">Modell</Label>
              <select
                id="settings-openai-model"
                data-testid="settings-openai-model"
                value={openaiModel}
                onChange={(e) => setOpenaiModel(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="gpt-5.6-terra">GPT-5.6 Terra (empfohlen)</option>
                <option value="gpt-5.6-luna">GPT-5.6 Luna (schnell)</option>
                <option value="gpt-5.6-sol">GPT-5.6 Sol (stärkste)</option>
                <option value="gpt-5.4">GPT-5.4</option>
                <option value="gpt-4.1">GPT-4.1</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                data-testid="settings-openai-save"
                onClick={() => saveOpenai(false)}
                disabled={openaiSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                {openaiSaving ? 'Speichert…' : 'Speichern'}
              </Button>
              {openai.source === 'settings' && openai.hint && (
                <Button
                  type="button"
                  variant="outline"
                  data-testid="settings-openai-clear"
                  onClick={() => saveOpenai(true)}
                  disabled={openaiSaving}
                >
                  Schlüssel entfernen
                </Button>
              )}
            </div>
          </div>
        </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="settings-email-notifications">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-brand-red/5 flex items-center justify-center">
              <span className="text-brand-red text-lg">✉️</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Benachrichtigungen</h2>
              <p className="text-sm text-slate-500">DSGVO-konform: nur Hinweis-Link, kein Inhalt in der Mail</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">E-Mail-Hinweis bei neuen Nachrichten</p>
              <p className="text-sm text-slate-600 mt-1">
                Erhalte eine kurze E-Mail, wenn dir jemand in der App schreibt — der Inhalt bleibt in der App.
                <br/>Maximal 1 Hinweis alle 5 Minuten pro Konversation.
              </p>
            </div>
            <Switch
              checked={emailNotifications}
              onCheckedChange={toggleEmailNotifications}
              data-testid="settings-email-notifications-toggle"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="settings-telephony">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-brand-red/5 flex items-center justify-center">
              <Phone className="w-5 h-5 text-brand-red" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Telefonie</h2>
              <p className="text-sm text-slate-500">
                Anrufe direkt aus der App starten — die Anlage ruft zuerst dein Telefon an
              </p>
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <div className="text-sm">
              <span className="text-slate-500">Dein Status für „{activeTenant?.name || 'diesen Mandanten'}“: </span>
              {dial.available ? (
                <span className="text-emerald-700">
                  Click-to-Dial aktiv, Kennung <code>{dial.sip_uid}</code>
                </span>
              ) : (
                <span className="text-slate-700">
                  keine Kennung hinterlegt — Telefonnummern öffnen das Telefonieprogramm des Rechners
                </span>
              )}
            </div>

            {!dial.api_configured && (
              <p className="text-sm text-amber-700">
                Der Placetel-API-Token fehlt noch auf dem Server (<code>PLACETEL_API_TOKEN</code>).
                Bis dahin bleibt Click-to-Dial für alle aus.
              </p>
            )}

            {!dial.can_manage && (
              <p className="text-sm text-slate-500">Kennungen werden von der Verwaltung gepflegt.</p>
            )}

            {dial.can_manage && (
              <div>
                <Label>Kennungen der Mitarbeitenden in diesem Mandanten</Label>
                <p className="text-sm text-slate-600 mt-1 mb-3">
                  Zur Auswahl stehen die VoIP-Ziele aus Placetel. Die Kennung gilt nur für
                  „{activeTenant?.name || 'diesen Mandanten'}“ — wer an mehreren Standorten
                  arbeitet, bekommt dort jeweils eine eigene.
                </p>
                {sipTargetsError && (
                  <p className="text-sm text-amber-700 mb-3" data-testid="settings-sip-targets-error">
                    {sipTargetsError} Solange von Hand eintragen: SIP-Benutzername und
                    SIP-Server stehen im Bearbeitungsformular des VoIP-Ziels im Placetel-Portal.
                  </p>
                )}
                <div className="space-y-2" data-testid="settings-sip-uid-list">
                  {dialUsers.map((u) => (
                    <div key={u.id} className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                      {sipTargets.length > 0 ? (
                        <select
                          value={u.sip_uid}
                          onChange={(e) => setSipUidDraft(u.id, e.target.value)}
                          className="w-64 h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid={`settings-sip-uid-input-${u.id}`}
                        >
                          <option value="">— keine Kennung —</option>
                          {/* Eine gespeicherte Kennung, die Placetel nicht mehr kennt, darf
                              nicht stillschweigend aus der Auswahl fallen. */}
                          {u.sip_uid && !sipTargets.some((t) => t.sipuid === u.sip_uid) && (
                            <option value={u.sip_uid}>{u.sip_uid} (in Placetel unbekannt)</option>
                          )}
                          {sipTargets.map((t) => (
                            <option key={t.sipuid} value={t.sipuid}>
                              {[t.name, t.did].filter(Boolean).join(' · ') || t.sipuid}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          value={u.sip_uid}
                          onChange={(e) => setSipUidDraft(u.id, e.target.value)}
                          placeholder="777z11aad121"
                          className="w-64 font-mono"
                          data-testid={`settings-sip-uid-input-${u.id}`}
                        />
                      )}
                      <Button
                        variant="outline"
                        onClick={() => saveSipUidFor(u.id, u.sip_uid)}
                        disabled={dialSaving === u.id}
                        data-testid={`settings-sip-uid-save-${u.id}`}
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {dialUsersError && (
                    <p className="text-sm text-rose-700" data-testid="settings-sip-uid-error">
                      {dialUsersError}
                    </p>
                  )}
                  {!dialUsersError && dialUsers.length === 0 && (
                    <p className="text-sm text-slate-500">Keine Benutzer in diesem Mandanten gefunden.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="settings-calendar-rooms">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-red/5 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Raumkalender für Lehrkräfte</h2>
                <p className="text-sm text-slate-500">
                  Lehrkräfte sehen zusätzlich zur eigenen Stunde, ob Räume frei oder belegt sind —
                  für Planungen wie Ballettgala oder Vorspiele. Die Detailtiefe gilt als Obergrenze;
                  in der Benutzerverwaltung kann die Rolle darunter bleiben (O/R/W/X).
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm text-slate-500">
                {calRooms.enabled ? 'Aktiv' : 'Inaktiv'}
              </span>
              <Switch
                checked={!!calRooms.enabled}
                onCheckedChange={(v) => setCalRooms((p) => ({ ...p, enabled: v }))}
                data-testid="settings-calendar-rooms-enabled"
              />
            </div>
          </div>
          {calRoomsLoading ? (
            <div className="text-center py-6 text-slate-500">Laden…</div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="mb-1 block">Maximale Detailtiefe</Label>
                <select
                  className="w-full max-w-md h-9 text-sm border border-slate-200 rounded-md px-2 bg-white"
                  value={calRooms.detail}
                  onChange={(e) => setCalRooms((p) => ({ ...p, detail: e.target.value }))}
                  disabled={!calRooms.enabled}
                  data-testid="settings-calendar-rooms-detail"
                >
                  <option value="busy">Nur frei / belegt (keine personenbezogenen Daten)</option>
                  <option value="course">Unterricht / Kurs (ohne Schüler:innen)</option>
                  <option value="students">Inkl. beteiligter Schüler:innen</option>
                </select>
              </div>
              <Button
                type="button"
                className="bg-brand-red hover:bg-brand-red/90"
                onClick={() => saveCalendarRoomsSettings()}
                disabled={calRoomsSaving}
                data-testid="settings-calendar-rooms-save"
              >
                {calRoomsSaving ? 'Speichern…' : 'Raumkalender speichern'}
              </Button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="settings-online-registration" id="online-anmeldung">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-red/5 flex items-center justify-center">
                <Globe className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Online-Anmeldung</h2>
                <p className="text-sm text-slate-500">
                  Konfiguriere den öffentlichen Anmelde-Wizard für „{activeTenant?.name || 'diesen Mandanten'}".
                  Pflichtangaben für die Wizard-Schritte (AGB · Datenschutz · Widerruf · Instrumente).
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm text-slate-500">
                {reg.registration_enabled ? 'Aktiv' : 'Inaktiv'}
              </span>
              <Switch
                checked={!!reg.registration_enabled}
                onCheckedChange={(v) => setReg((p) => ({ ...p, registration_enabled: v }))}
                data-testid="settings-registration-enabled-toggle"
              />
            </div>
          </div>

          <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" data-testid="settings-registration-guide">
            <p className="font-semibold text-slate-900 mb-2">Wo wird was gepflegt?</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Diese Seite: Begrüßungstext, AGB/Datenschutz/Widerruf, Fächerliste, Schritte und Button-Text.</li>
              <li>Darunter „Formular bearbeiten“: Feld-Beschriftungen, sichtbare Schritte, Absende-Hinweis und ob zuerst die Lehrkraft angefragt wird.</li>
              <li>Darunter „Unterrichtsarten“: Preise und Unterrichtsformen dieses Mandanten.</li>
              <li>
                Bestätigungs-E-Mail:
                {' '}
                <a href="/correspondence" className="text-brand-red underline">Korrespondenz</a>
                {' '}
                → Systemvorlage „Anmeldebestätigung Online-Anmeldung“.
              </li>
              <li>
                Eingegangene Anmeldungen:
                {' '}
                <a href="/registrations-inbox" className="text-brand-red underline">Online-Anmeldungen</a>
                .
              </li>
              <li>Kursangebote im Formular: Unterrichtsverwaltung → Kurs → Veröffentlichung in der Online-Anmeldung.</li>
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              Fächer, Orte und Angebote gehören immer zum aktuell gewählten Mandanten. Einträge, die nicht in den Stammdaten dieses Mandanten vorkommen, erscheinen nicht im öffentlichen Formular.
            </p>
          </div>

          {regLoading ? (
            <div className="text-center py-8 text-slate-500">Laden…</div>
          ) : (
            <div className="space-y-6">
              <div>
                <Label className="mb-1 block">Begrüßungstext (Wizard-Intro)</Label>
                <Textarea
                  rows={3}
                  value={reg.registration_intro || ''}
                  onChange={(e) => setReg((p) => ({ ...p, registration_intro: e.target.value }))}
                  placeholder="Willkommen zur Anmeldung an unserer Musikschule …"
                  data-testid="settings-registration-intro"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">AGB-Text</Label>
                  <Textarea
                    rows={4}
                    value={reg.agb_text || ''}
                    onChange={(e) => setReg((p) => ({ ...p, agb_text: e.target.value }))}
                    placeholder="Allgemeine Geschäftsbedingungen …"
                    data-testid="settings-agb-text"
                  />
                </div>
                <div>
                  <Label className="mb-1 block">AGB-Link (optional)</Label>
                  <Input
                    type="url"
                    value={reg.agb_url || ''}
                    onChange={(e) => setReg((p) => ({ ...p, agb_url: e.target.value }))}
                    placeholder="https://…"
                    data-testid="settings-agb-url"
                  />
                </div>

                <div>
                  <Label className="mb-1 block">Datenschutz-Text</Label>
                  <Textarea
                    rows={4}
                    value={reg.datenschutz_text || ''}
                    onChange={(e) => setReg((p) => ({ ...p, datenschutz_text: e.target.value }))}
                    placeholder="Datenschutzhinweise nach Art. 13 DSGVO …"
                    data-testid="settings-datenschutz-text"
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Datenschutz-Link (optional)</Label>
                  <Input
                    type="url"
                    value={reg.datenschutz_url || ''}
                    onChange={(e) => setReg((p) => ({ ...p, datenschutz_url: e.target.value }))}
                    placeholder="https://…"
                    data-testid="settings-datenschutz-url"
                  />
                </div>

                <div>
                  <Label className="mb-1 block">Widerrufsbelehrung</Label>
                  <Textarea
                    rows={4}
                    value={reg.widerruf_text || ''}
                    onChange={(e) => setReg((p) => ({ ...p, widerruf_text: e.target.value }))}
                    placeholder="Sie haben das Recht, binnen 14 Tagen …"
                    data-testid="settings-widerruf-text"
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Widerrufs-Link (optional)</Label>
                  <Input
                    type="url"
                    value={reg.widerruf_url || ''}
                    onChange={(e) => setReg((p) => ({ ...p, widerruf_url: e.target.value }))}
                    placeholder="https://…"
                    data-testid="settings-widerruf-url"
                  />
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">Instrumenten-Kategorien</h3>
                    <p className="text-sm text-slate-500">Auswahl im Wizard-Schritt 1 (z.B. „Streicher" → Geige, Bratsche, Cello).</p>
                  </div>
                </div>

                <div className="flex gap-2 mb-4">
                  <Input
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="Neue Kategorie (z.B. Streicher)"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInstrumentCategory(); } }}
                    data-testid="settings-new-category-input"
                  />
                  <Button onClick={addInstrumentCategory} variant="outline" data-testid="settings-add-category-btn">
                    <Plus className="h-4 w-4 mr-1" /> Kategorie
                  </Button>
                </div>

                {reg.instrument_categories.length === 0 ? (
                  <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
                    Noch keine Kategorien angelegt. Beispiele: „Streicher", „Holzbläser", „Blechbläser", „Tasten", „Zupf", „Schlagzeug", „Gesang".
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reg.instrument_categories.map((cat, idx) => (
                      <CategoryRow
                        key={`${cat.name}-${idx}`}
                        category={cat}
                        onRemove={() => removeInstrumentCategory(idx)}
                        onAddItem={(v) => addInstrumentToCategory(idx, v)}
                        onRemoveItem={(itemIdx) => removeInstrumentFromCategory(idx, itemIdx)}
                        testIdPrefix={`settings-category-${idx}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={saveRegistrationSettings}
                  disabled={regSaving}
                  data-testid="settings-registration-save-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {regSaving ? 'Speichern…' : 'Online-Anmeldung speichern'}
                </Button>
              </div>

              <div className="border-t pt-6" id="online-anmeldung-formular">
                <RegistrationFormEditor tenantId={activeTenantId} />
              </div>

              <div className="border-t pt-6">
                <TeachingConfigEditor tenantId={activeTenantId} />
              </div>

              <div className="border-t pt-6 mt-2">
                <h3 className="font-semibold text-slate-900 mb-1">SLA-Erinnerung</h3>
                <p className="text-sm text-slate-500 mb-3">
                  Sobald eine bestätigte Anmeldung länger als die hier festgelegte Anzahl <strong>Werktage</strong> in der Inbox liegt, geht eine Erinnerung an die Verwaltung. <em>0 = aus.</em>
                </p>
                <div className="flex items-center gap-2 max-w-xs">
                  <Input
                    type="number"
                    min={0}
                    max={30}
                    value={reg.registration_sla_days}
                    onChange={(e) => setReg((p) => ({ ...p, registration_sla_days: Math.max(0, Math.min(30, parseInt(e.target.value, 10) || 0)) }))}
                    className="w-24"
                    data-testid="settings-registration-sla-days"
                  />
                  <span className="text-sm text-slate-500">Werktage</span>
                </div>

                <SchedulerRunsPanel />
              </div>

              {reg.registration_enabled && activeTenant?.slug && (
                <RegistrationShareCard tenantSlug={activeTenant.slug} tenantName={activeTenant.name} />
              )}
            </div>
          )}
        </div>

        <CustomFieldsManager />

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-brand-red" />
            <h2 className="text-xl font-semibold text-slate-900">Rollen & Berechtigungen</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Die Rollenverwaltung ist bereits konfiguriert. Folgende Rollen sind verfügbar:
          </p>
          <div className="space-y-3">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">Admin</p>
              <p className="text-sm text-slate-600">Voller Zugriff auf alle Funktionen</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">Sekretariat</p>
              <p className="text-sm text-slate-600">Verwaltung von Schülern, Kursen und Standorten</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">Lehrkraft</p>
              <p className="text-sm text-slate-600">Zugriff auf eigene Kurse und Schüler</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}


function RegistrationShareCard({ tenantSlug, tenantName }) {
  const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const wizardUrl = `${baseUrl}/anmeldung/${encodeURIComponent(tenantSlug)}`;
  const iframeSnippet = `<iframe src="${wizardUrl}" width="100%" height="1100" style="border:0" title="Online-Anmeldung ${tenantName || ''}"></iframe>`;

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error('Kopieren nicht möglich');
    }
  };

  const downloadQr = () => {
    const svgEl = document.getElementById('registration-qr-svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svgEl);
    const img = new Image();
    const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 512; canvas.height = 512;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `anmeldung-${tenantSlug}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    };
    img.src = url;
  };

  return (
    <div className="border-t pt-6 mt-6" data-testid="settings-registration-share">
      <h3 className="font-semibold text-slate-900 mb-1">Teilen & Einbetten</h3>
      <p className="text-sm text-slate-500 mb-4">
        QR-Code für Flyer / Konzerte / Tag der offenen Tür — oder als iframe in die Wordpress-Site einbauen.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 border border-slate-200 rounded-xl p-4 bg-white flex flex-col items-center justify-center gap-3">
          <div className="bg-white p-3 rounded-lg border border-slate-100">
            <QRCodeSVG
              id="registration-qr-svg"
              value={wizardUrl}
              size={160}
              level="M"
              includeMargin={false}
            />
          </div>
          <Button variant="outline" size="sm" onClick={downloadQr} data-testid="settings-registration-qr-download">
            QR-Code als PNG speichern
          </Button>
        </div>

        <div className="md:col-span-2 space-y-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Direkter Link zum Wizard</Label>
            <div className="flex gap-2">
              <Input value={wizardUrl} readOnly className="font-mono text-xs" data-testid="settings-registration-url" />
              <Button variant="outline" onClick={() => copy(wizardUrl, 'Link')} data-testid="settings-registration-url-copy">
                Kopieren
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">iframe-Code für Wordpress (o. ä.)</Label>
            <div className="flex gap-2">
              <Textarea value={iframeSnippet} readOnly rows={3} className="font-mono text-xs" data-testid="settings-registration-iframe" />
              <Button variant="outline" onClick={() => copy(iframeSnippet, 'iframe-Code')} data-testid="settings-registration-iframe-copy">
                Kopieren
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SchedulerRunsPanel() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/registrations/scheduler-runs?limit=5');
      setRuns(r.data);
    } catch {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const labelFor = (jobName) => jobName === 'sla_daily' ? 'Daily-Job' : jobName === 'sla_manual' ? 'Manuell' : jobName;

  return (
    <div className="mt-6 pt-4 border-t border-dashed border-slate-200" data-testid="settings-scheduler-runs">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Letzte Scheduler-Läufe</h4>
          <p className="text-xs text-slate-500">Automatische SLA-Reminder-Durchläufe. Werktags zwischen 08:00–09:00.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="settings-scheduler-runs-refresh">
          Aktualisieren
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-slate-400">Laden…</div>
      ) : runs.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Noch keine Läufe protokolliert.</div>
      ) : (
        <ul className="text-xs space-y-1.5 mt-2">
          {runs.map((r) => {
            const when = formatDateTime(r.started_at);
            const result = r.result || {};
            const failed = r.status === 'failed';
            return (
              <li
                key={r.id}
                className={`flex items-center gap-3 px-3 py-2 rounded border ${failed ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}
                data-testid={`settings-scheduler-run-${r.id}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${failed ? 'bg-red-500' : 'bg-emerald-500'}`} />
                <span className="font-mono text-slate-500 shrink-0">{when}</span>
                <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] uppercase tracking-wide text-slate-500 shrink-0">
                  {labelFor(r.job_name)}
                </span>
                {failed ? (
                  <span className="text-red-600 truncate">Fehler: {r.error_message || 'unbekannt'}</span>
                ) : (
                  <span className="text-slate-700">
                    {typeof result.overdue === 'number' ? `${result.overdue} überfällig · ` : ''}
                    {typeof result.sent === 'number' ? `${result.sent} gesendet · ` : ''}
                    {typeof result.skipped === 'number' ? `${result.skipped} geskippt` : ''}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CategoryRow({ category, onRemove, onAddItem, onRemoveItem, testIdPrefix }) {  const [item, setItem] = useState('');
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-slate-900">{vokalSpelling(category.name)}</div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-red-600 hover:text-red-700"
          data-testid={`${testIdPrefix}-remove`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {(category.items || []).length === 0 ? (
          <span className="text-xs text-slate-500">Noch keine Instrumente</span>
        ) : (
          (category.items || []).map((it, i) => (
            <Badge key={`${it}-${i}`} variant="secondary" className="gap-1 pr-1">
              {it}
              <button
                onClick={() => onRemoveItem(i)}
                className="hover:bg-slate-300 rounded-full p-0.5"
                aria-label={`Instrument ${it} entfernen`}
                data-testid={`${testIdPrefix}-item-${i}-remove`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={item}
          onChange={(e) => setItem(e.target.value)}
          placeholder="Instrument hinzufügen (z.B. Geige)"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAddItem(item);
              setItem('');
            }
          }}
          data-testid={`${testIdPrefix}-item-input`}
        />
        <Button
          variant="outline"
          onClick={() => { onAddItem(item); setItem(''); }}
          data-testid={`${testIdPrefix}-item-add`}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
